import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import React from 'react';

import { logger } from '@/lib/logger';

type SaveState = 'idle' | 'pending' | 'saving' | 'error';

/**
 * Live content returned by components for stale data validation.
 * Contains action ID and current payload state from the editor.
 */
export interface LiveActionContent {
  actionId: number;
  payload: Record<string, unknown>;
  /** Stable list-position used to match live content to the current server
   * action across version bumps (id changes on a version bump; this doesn't). */
  executionOrder?: number;
}

interface ComponentRegistration {
  saveState: SaveState;
  flushFn: (() => Promise<void>) | null;
  getContentFn: (() => LiveActionContent | null) | null;
  resyncFn: (() => void) | null;
}

interface SubscriberState {
  hasPendingOrSaving: boolean;
  hasError: boolean;
  /** Monotonically increasing counter that increments on every save state change.
   * Used by consumers (e.g., StickyFooter) to re-render when editor content changes,
   * even when hasPendingOrSaving doesn't toggle. */
  contentVersion: number;
  /** True while a decision (approve) holds the editors locked — see `lockForDecision`. */
  isDecisionLocked: boolean;
}

interface RegistrationHandle {
  setSaveState: (state: SaveState) => void;
  setFlushFn: (fn: () => Promise<void>) => void;
  setContentFn: (fn: () => LiveActionContent | null) => void;
  setResyncFn: (fn: () => void) => void;
  unregister: () => void;
}

export interface FlushResult {
  success: boolean;
  errors: Error[];
}

export interface SettleAndFreezeResult extends FlushResult {
  /** Freeze-owner token when `success`; null when the settle failed (no freeze taken). */
  freezeToken: number | null;
}

interface SettleOptions {
  /** Registrations to skip — both for flushing and for the idle check. */
  excludeIds?: string[];
}

/**
 * Overall budget for `flushUntilIdle`. Each individual flush already carries a
 * 5s timeout inside `flushAllAndWait`; this bounds the loop that waits out
 * registrations which report `saving` without a flush function (one-shot saves)
 * or re-arm while an earlier flush is in flight.
 */
const SETTLE_DEADLINE_MS = 10000;

export interface AutoSaveCoordinationContextValue {
  /**
   * Register a component for save state coordination.
   * Returns handle with setSaveState, setFlushFn, setContentFn, and unregister functions.
   */
  register: (id: string) => RegistrationHandle;
  /**
   * Subscribe to aggregated state changes.
   * Returns unsubscribe function.
   */
  subscribe: (callback: (state: SubscriberState) => void) => () => void;
  /**
   * Flush all registered components and wait for completion.
   * Returns success status and any errors encountered.
   *
   * Pass `excludeIds` to skip specific registrations — e.g. a component that
   * owns its own final-commit flush (and would otherwise double-save if its
   * registered flushFn also ran here).
   */
  flushAllAndWait: (options?: {
    excludeIds?: string[];
  }) => Promise<FlushResult>;
  /**
   * Collect current content from all registered components.
   * Used by StickyFooter to get live editor content for stale data validation.
   */
  collectAllContent: () => LiveActionContent[];
  /**
   * Freeze autosave across all registered components. Called by the approval
   * footers right before the approve/reject POST — and by a card's own "I don't
   * have this information" exit right before its reassign/reject POST: once the
   * proposal leaves the `pending` state, any update_payload call is rejected by
   * the backend (`UPDATE_FAILED`), so components must neither arm new debounce
   * timers nor let already-armed timers fire. Components check
   * `isAutoSaveFrozen()` at both points. The flush-before-approval path is
   * unaffected — it runs before the freeze.
   *
   * Returns a unique owner token identifying THIS freeze. The freeze is
   * reference-counted across all live owners: multiple independent decisions
   * (a footer approve/reject AND a card exit) can hold it at once, and it stays
   * frozen until EVERY owner has released. Pass the token back to
   * `unfreezeAutoSave(token)` so each caller releases only its own hold —
   * releasing one owner never clears a freeze another owner still needs held,
   * regardless of the order in which they resolve.
   */
  freezeAutoSave: () => number;
  /**
   * Release this caller's hold on the autosave freeze. Called when an
   * approve/reject/exit attempt fails terminally, so the user can resume
   * editing and retry.
   *
   * Pass the token returned by the matching `freezeAutoSave()`: it removes ONLY
   * this owner from the active set, and the freeze lifts only once the LAST
   * owner releases. This is order-independent — an owner that resolves before
   * another still-in-flight owner cannot lift a freeze that owner needs held.
   * A tokenless call releases unconditionally, clearing every owner (explicit
   * navigation cleanup / legacy callers).
   */
  unfreezeAutoSave: (token?: number) => void;
  /** Whether autosave is currently frozen (read at arm time and fire time). */
  isAutoSaveFrozen: () => boolean;
  /**
   * Force every registered component to resync with the latest known server
   * content, even if it is mid-editing-session. Used by STALE_ACTIONS
   * recovery to show the user what changed, in place, for editors that are
   * still mounted.
   */
  resyncFromServer: () => void;
  /**
   * Lock the editors for a decision (approve). From the Approve click until the
   * decision dispatches or is abandoned, editors render read-only (via
   * `useDecisionLocked`) and coordinated autosave HOLDS new edits as pending
   * instead of arming a debounce — so the "all saves finished" state a decision
   * waited for cannot silently stop being true underneath it. Unlike the
   * freeze, flush writes are still allowed while locked.
   *
   * Reference-counted by token, like the freeze: `releaseDecisionLock(token)`
   * releases only that owner; a tokenless call releases every owner.
   */
  lockForDecision: () => number;
  releaseDecisionLock: (token?: number) => void;
  /** Call-time read of the decision lock (for event handlers and timers). */
  isDecisionLocked: () => boolean;
  /**
   * Flush every registration and keep going until the aggregate reports no
   * `pending`/`saving` — not merely until one flush pass returns. A pass can
   * finish while a registration without a flush function is still saving, or
   * while an edit re-armed during the pass. Fails when any flush fails, or when
   * the rail is still busy at the deadline.
   */
  flushUntilIdle: (options?: SettleOptions) => Promise<FlushResult>;
  /**
   * `flushUntilIdle`, then — in the same synchronous step as the final idle
   * check — `freezeAutoSave()`. This is the only sanctioned way for a decision
   * to take the freeze: nothing can arm between "saves are done" and "saves are
   * frozen", so a dispatched decision always leaves an idle rail behind (and
   * the unsaved-changes guard has nothing to fire on).
   */
  settleAndFreeze: (options?: SettleOptions) => Promise<SettleAndFreezeResult>;
}

const AutoSaveCoordinationContext =
  createContext<AutoSaveCoordinationContextValue | null>(null);

/**
 * Data context for the decision lock, split from the actions context above so
 * that components which only call actions don't re-render when the lock flips
 * (REACT_19_PATTERNS § 2.3). Defaults to unlocked when no provider is mounted.
 */
const DecisionLockContext = createContext(false);

interface AutoSaveCoordinationProviderProps {
  children: ReactNode;
}

/**
 * Provider that coordinates auto-save state across preview components.
 *
 * Preview components register themselves and report their save state.
 * StickyFooter subscribes to aggregate state and can trigger flush before approval.
 *
 * Uses subscriber pattern with useRef to avoid re-renders on state changes.
 *
 * @example
 * ```tsx
 * <AutoSaveCoordinationProvider>
 *   <EmailActionPreview />
 *   <StickyFooter />
 * </AutoSaveCoordinationProvider>
 * ```
 */
export function AutoSaveCoordinationProvider({
  children,
}: AutoSaveCoordinationProviderProps) {
  // Store registrations in ref to avoid re-renders (like useProposalRefresh pattern)
  const registrationsRef = useRef<Map<string, ComponentRegistration>>(
    new Map()
  );
  // Store subscribers in ref
  const subscribersRef = useRef<Set<(state: SubscriberState) => void>>(
    new Set()
  );
  // Monotonically increasing counter for content change tracking
  const contentVersionRef = useRef(0);
  // Frozen flag — true while an approve/reject is in flight or has resolved
  // the proposal. Ref-based so reads/writes never trigger re-renders.
  const frozenRef = useRef(false);
  // Monotonic counter that mints a unique token for every freezeAutoSave()
  // call. Never reused, so tokens can't collide across independent freezers.
  const freezeOwnerRef = useRef(0);
  // The set of tokens whose freeze is still live. The freeze is reference-
  // counted: freezeAutoSave() adds a token, unfreezeAutoSave(token) removes it,
  // and frozenRef stays true until the set is empty. This lets independent
  // freezers (the approval footers AND a card's own "I don't have this
  // information" exit) share one provider-wide freeze without clobbering each
  // other in ANY resolution order — an owner releasing its own token never
  // lifts a freeze another owner still holds.
  const activeFreezeOwnersRef = useRef<Set<number>>(new Set());
  // Decision lock — same token/ref-count shape as the freeze. The ref serves
  // call-time reads (event handlers, timers); the state feeds DecisionLockContext
  // so editors re-render read-only.
  const decisionLockedRef = useRef(false);
  const decisionLockOwnerRef = useRef(0);
  const activeDecisionLockOwnersRef = useRef<Set<number>>(new Set());
  const [decisionLocked, setDecisionLocked] = useState(false);

  // Compute aggregate state from registrations, optionally ignoring some ids
  // (settle callers that exclude a registration from the flush must also exclude
  // it from the idle check).
  const computeState = useCallback(
    (excludeIds?: ReadonlySet<string>): SubscriberState => {
      let hasPendingOrSaving = false;
      let hasError = false;

      registrationsRef.current.forEach((registration, id) => {
        if (excludeIds?.has(id)) {
          return;
        }
        if (
          registration.saveState === 'pending' ||
          registration.saveState === 'saving'
        ) {
          hasPendingOrSaving = true;
        }
        if (registration.saveState === 'error') {
          hasError = true;
        }
      });

      return {
        hasPendingOrSaving,
        hasError,
        contentVersion: contentVersionRef.current,
        isDecisionLocked: decisionLockedRef.current,
      };
    },
    []
  );

  // Notify all subscribers of state change
  const notifySubscribers = useCallback(() => {
    contentVersionRef.current++;
    const state = computeState();
    subscribersRef.current.forEach(callback => {
      try {
        callback(state);
      } catch (e) {
        logger.error(
          'Subscriber error',
          e instanceof Error ? e : new Error(String(e)),
          { component: 'proposals', module: 'useAutoSaveCoordination' }
        );
      }
    });
  }, [computeState]);

  const register = useCallback(
    (id: string): RegistrationHandle => {
      // Initialize registration
      registrationsRef.current.set(id, {
        saveState: 'idle',
        flushFn: null,
        getContentFn: null,
        resyncFn: null,
      });

      const setSaveState = (state: SaveState) => {
        const registration = registrationsRef.current.get(id);
        if (registration) {
          registration.saveState = state;
          notifySubscribers();
        }
      };

      const setFlushFn = (fn: () => Promise<void>) => {
        const registration = registrationsRef.current.get(id);
        if (registration) {
          registration.flushFn = fn;
        }
      };

      const setContentFn = (fn: () => LiveActionContent | null) => {
        const registration = registrationsRef.current.get(id);
        if (registration) {
          registration.getContentFn = fn;
        }
      };

      const setResyncFn = (fn: () => void) => {
        const registration = registrationsRef.current.get(id);
        if (registration) {
          registration.resyncFn = fn;
        }
      };

      const unregister = () => {
        registrationsRef.current.delete(id);
        notifySubscribers();
      };

      return {
        setSaveState,
        setFlushFn,
        setContentFn,
        setResyncFn,
        unregister,
      };
    },
    [notifySubscribers]
  );

  const subscribe = useCallback(
    (callback: (state: SubscriberState) => void) => {
      subscribersRef.current.add(callback);
      // Immediately notify subscriber of current state so they don't miss in-progress saves
      callback(computeState());
      return () => {
        subscribersRef.current.delete(callback);
      };
    },
    [computeState]
  );

  const flushAllAndWait = useCallback(
    async (options?: { excludeIds?: string[] }): Promise<FlushResult> => {
      const excludeIds = new Set(options?.excludeIds ?? []);
      const flushFunctions: (() => Promise<void>)[] = [];

      registrationsRef.current.forEach((registration, id) => {
        if (registration.flushFn && !excludeIds.has(id)) {
          flushFunctions.push(registration.flushFn);
        }
      });

      logger.debug('Flushing components', {
        component: 'autosave',
        count: flushFunctions.length,
      });

      if (flushFunctions.length === 0) {
        logger.debug('Flush completed', {
          component: 'autosave',
          succeeded: 0,
          failed: 0,
        });
        return { success: true, errors: [] };
      }

      // Wrap each flush with a 5-second timeout
      const FLUSH_TIMEOUT_MS = 5000;
      const wrappedFlushes = flushFunctions.map(flushFn => {
        return Promise.race([
          flushFn(),
          new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error('Flush timeout (5s)')),
              FLUSH_TIMEOUT_MS
            )
          ),
        ]);
      });

      const results = await Promise.allSettled(wrappedFlushes);

      const errors: Error[] = [];
      results.forEach(result => {
        if (result.status === 'rejected') {
          const error =
            result.reason instanceof Error
              ? result.reason
              : new Error(String(result.reason));
          errors.push(error);
        }
      });

      const success = errors.length === 0;
      const succeeded = results.length - errors.length;
      const failed = errors.length;
      logger.debug('Flush completed', {
        component: 'autosave',
        succeeded,
        failed,
        errorCount: errors.length,
      });

      return { success, errors };
    },
    []
  );

  const freezeAutoSave = useCallback(() => {
    freezeOwnerRef.current += 1;
    const token = freezeOwnerRef.current;
    activeFreezeOwnersRef.current.add(token);
    frozenRef.current = true;
    return token;
  }, []);

  const unfreezeAutoSave = useCallback((token?: number) => {
    // A tokenless call (explicit navigation cleanup / legacy callers) releases
    // unconditionally, dropping every owner. A tokened call removes only that
    // owner from the active set; the freeze lifts only once the set is empty,
    // so an owner that resolves while another is still in flight never clears a
    // freeze the other owner still needs held — order-independent by construction.
    if (token === undefined) {
      activeFreezeOwnersRef.current.clear();
    } else {
      activeFreezeOwnersRef.current.delete(token);
    }
    if (activeFreezeOwnersRef.current.size === 0) {
      frozenRef.current = false;
    }
  }, []);

  const isAutoSaveFrozen = useCallback(() => frozenRef.current, []);

  const setDecisionLockedTo = useCallback(
    (locked: boolean) => {
      if (decisionLockedRef.current === locked) {
        return;
      }
      decisionLockedRef.current = locked;
      setDecisionLocked(locked);
      // Coordinated editors subscribe to re-arm edits they held while locked.
      notifySubscribers();
    },
    [notifySubscribers]
  );

  const lockForDecision = useCallback(() => {
    decisionLockOwnerRef.current += 1;
    const token = decisionLockOwnerRef.current;
    activeDecisionLockOwnersRef.current.add(token);
    setDecisionLockedTo(true);
    return token;
  }, [setDecisionLockedTo]);

  const releaseDecisionLock = useCallback(
    (token?: number) => {
      if (token === undefined) {
        activeDecisionLockOwnersRef.current.clear();
      } else {
        activeDecisionLockOwnersRef.current.delete(token);
      }
      if (activeDecisionLockOwnersRef.current.size === 0) {
        setDecisionLockedTo(false);
      }
    },
    [setDecisionLockedTo]
  );

  const isDecisionLocked = useCallback(() => decisionLockedRef.current, []);

  // Resolves on the next aggregate state change, or after `timeoutMs`. Added to
  // the subscriber set directly (not via `subscribe`) so it isn't invoked
  // immediately with the current state.
  const waitForStateChange = useCallback((timeoutMs: number) => {
    return new Promise<void>(resolve => {
      const done = () => {
        clearTimeout(timer);
        subscribersRef.current.delete(done);
        resolve();
      };
      const timer = setTimeout(done, timeoutMs);
      subscribersRef.current.add(done);
    });
  }, []);

  // Shared core of flushUntilIdle / settleAndFreeze. `onIdle` runs in the SAME
  // synchronous step as the final idle check — no await in between — so nothing
  // can arm between "the rail is idle" and whatever the caller does next.
  const settle = useCallback(
    async (
      options: SettleOptions | undefined,
      onIdle: () => void
    ): Promise<FlushResult> => {
      const excludeIds = new Set(options?.excludeIds ?? []);
      const deadline = Date.now() + SETTLE_DEADLINE_MS;
      for (;;) {
        // Snapshot the state counter so we can tell whether anything moved while
        // this pass ran (e.g. an edit re-armed mid-flush). Its notification has
        // already fired by the time the pass returns, so waiting for the "next"
        // change would hang — re-evaluate with another pass instead.
        const versionBeforePass = contentVersionRef.current;
        const result = await flushAllAndWait(options);
        if (!result.success) {
          return result;
        }
        const { hasPendingOrSaving, hasError } = computeState(excludeIds);
        if (!hasPendingOrSaving && !hasError) {
          onIdle();
          return { success: true, errors: [] };
        }
        if (hasError && !hasPendingOrSaving) {
          // Errored registration whose flush didn't clear it (or that has no
          // flush function) — nothing further will change without the user.
          return {
            success: false,
            errors: [new Error('A change could not be saved')],
          };
        }
        const remaining = deadline - Date.now();
        if (remaining <= 0) {
          logger.warn('Settle deadline reached with saves still in progress', {
            component: 'autosave',
          });
          return {
            success: false,
            errors: [new Error('Changes are still saving')],
          };
        }
        if (contentVersionRef.current !== versionBeforePass) {
          continue;
        }
        // Nothing moved during the pass: the busy registration is one a flush
        // can't advance (in-flight write, or one-shot save without a flush
        // function). Wait for it to report a change.
        await waitForStateChange(Math.min(remaining, 1000));
      }
    },
    [computeState, flushAllAndWait, waitForStateChange]
  );

  const flushUntilIdle = useCallback(
    (options?: SettleOptions) => settle(options, () => {}),
    [settle]
  );

  const settleAndFreeze = useCallback(
    async (options?: SettleOptions): Promise<SettleAndFreezeResult> => {
      let freezeToken: number | null = null;
      const result = await settle(options, () => {
        freezeToken = freezeAutoSave();
      });
      return { ...result, freezeToken };
    },
    [settle, freezeAutoSave]
  );

  const resyncFromServer = useCallback(() => {
    registrationsRef.current.forEach(registration => {
      try {
        registration.resyncFn?.();
      } catch (e) {
        logger.error(
          'Resync error',
          e instanceof Error ? e : new Error(String(e)),
          { component: 'proposals', module: 'useAutoSaveCoordination' }
        );
      }
    });
  }, []);

  const collectAllContent = useCallback((): LiveActionContent[] => {
    const results: LiveActionContent[] = [];

    registrationsRef.current.forEach(registration => {
      if (registration.getContentFn) {
        const content = registration.getContentFn();
        if (content) {
          results.push(content);
        }
      }
    });

    return results;
  }, []);

  const contextValue = useMemo(
    () => ({
      register,
      subscribe,
      flushAllAndWait,
      collectAllContent,
      freezeAutoSave,
      unfreezeAutoSave,
      isAutoSaveFrozen,
      resyncFromServer,
      lockForDecision,
      releaseDecisionLock,
      isDecisionLocked,
      flushUntilIdle,
      settleAndFreeze,
    }),
    [
      register,
      subscribe,
      flushAllAndWait,
      collectAllContent,
      freezeAutoSave,
      unfreezeAutoSave,
      isAutoSaveFrozen,
      resyncFromServer,
      lockForDecision,
      releaseDecisionLock,
      isDecisionLocked,
      flushUntilIdle,
      settleAndFreeze,
    ]
  );

  return React.createElement(
    AutoSaveCoordinationContext,
    { value: contextValue },
    React.createElement(
      DecisionLockContext,
      { value: decisionLocked },
      children
    )
  );
}

/**
 * Whether a decision currently holds the editors locked (see `lockForDecision`).
 * Editors fold this into their read-only state. False when no provider is
 * mounted — an editor outside a decision surface is never decision-locked.
 */
export function useDecisionLocked(): boolean {
  return useContext(DecisionLockContext);
}

/**
 * Hook to access auto-save coordination functionality.
 *
 * Must be used within an AutoSaveCoordinationProvider.
 * Throws if no provider is found.
 *
 * @returns Context value with register, subscribe, and flushAllAndWait
 */
export function useAutoSaveCoordination(): AutoSaveCoordinationContextValue {
  const context = useContext(AutoSaveCoordinationContext);
  if (!context) {
    throw new Error(
      'useAutoSaveCoordination must be used within AutoSaveCoordinationProvider'
    );
  }
  return context;
}

/**
 * Hook to optionally access auto-save coordination functionality.
 *
 * Returns null if no provider is found (graceful fallback).
 * Use this in StickyFooter to handle cases where coordination is not available.
 *
 * @returns Context value or null if no provider
 */
export function useAutoSaveCoordinationOptional(): AutoSaveCoordinationContextValue | null {
  return useContext(AutoSaveCoordinationContext);
}
